import { IFeedItemToken } from '@app/models/FeedItemToken'
import { bindFunctions } from '@app/utils/class'
import classNames from 'classnames'
import decorateComponentWithProps from 'decorate-component-with-props'
import { EditorState } from 'draft-js'
import Editor from 'draft-js-plugins-editor'
import createMentionPlugin, {
  DraftJsMentionPlugin,
} from 'gateway-draft-js-mention-plugin'
import 'gateway-draft-js-mention-plugin/lib/plugin.css'
import { List } from 'immutable'
import * as React from 'react'
import NoSuggestions from '../containers/NoSuggestionsContainer'
import * as MentionEditorModule from '../modules/mentionEditor'
import FeedItemTokenSuggestion from './FeedItemTokenSuggestion'

import './MentionEditor.scss'

export interface IMentionEditorUIProps {
  id: string
  compact?: boolean
  classNames?: string
  editorState: EditorState
  onChange: (editorState: EditorState) => void
  placeholder?: string
}

export interface IMentionEditorModuleProps {
  suggestions: List<IFeedItemToken>
}

type MentionEditorProps = IMentionEditorUIProps &
  IMentionEditorModuleProps &
  typeof MentionEditorModule.actionCreators

export default class MentionEditor extends React.PureComponent<
  MentionEditorProps
> {
  private editor: React.RefObject<Editor>
  private mentionSuggestions: React.RefObject<
    DraftJsMentionPlugin.MentionSuggestions
  >
  private mentionPlugin: DraftJsMentionPlugin.IMentionsPlugin
  private noSuggestionsComponent: any

  constructor(props: MentionEditorProps) {
    super(props)

    this.mentionPlugin = createMentionPlugin({
      entityMutability: 'IMMUTABLE',
      supportWhitespace: true,
      theme: {
        mention: 'mention',
        mentionSuggestions: 'mention-suggestions',
        mentionSuggestionsEntry: 'mention-suggestions-entry',
        mentionSuggestionsEntryAvatar: 'mention-suggestions-entry-avatar',
        mentionSuggestionsEntryFocused: 'mention-suggestions-entry-focused',
        mentionSuggestionsEntryText: 'mention-suggestions-entry-text',
      },
    })
    this.editor = React.createRef<Editor>()
    this.mentionSuggestions = React.createRef<
      DraftJsMentionPlugin.MentionSuggestions
    >()
    this.noSuggestionsComponent = decorateComponentWithProps(NoSuggestions, {
      editorId: this.props.id,
    })

    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.initEditor(this.props.id)
  }

  public componentWillUnmount() {
    this.props.removeEditor(this.props.id)
  }

  public render() {
    const { MentionSuggestions } = this.mentionPlugin
    const plugins = [this.mentionPlugin]

    const editorClasses = classNames('mention-editor', this.props.classNames, {
      'mention-editor-compact': this.props.compact,
    })

    return (
      <div className={editorClasses}>
        <Editor
          editorState={this.props.editorState}
          onChange={this.props.onChange}
          placeholder={this.props.placeholder}
          plugins={plugins}
          ref={this.editor}
          spellCheck
        />
        <MentionSuggestions
          onSearchChange={this._onSearchChange}
          suggestions={this.props.suggestions.toArray()}
          onAddMention={this._onAddMention}
          entryComponent={FeedItemTokenSuggestion}
          noSuggestionsComponent={this.noSuggestionsComponent}
        />
      </div>
    )
  }

  private _onAddMention(mention: IFeedItemToken) {
    this.props.clearSuggestions(this.props.id)
  }

  private _onSearchChange({ value }: DraftJsMentionPlugin.SearchChange) {
    this.props.fetchSuggestions(this.props.id, value)
  }
}
